import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ShotPlan } from '@/store/slices/creativeJobs';
import { Field, TextArea } from '@/nova/components';

import { updateShot } from '../../storyboard';
import { ClosingTag, Root, Shot, ShotFields, ShotHead, ShotLabel, Shots, TimeChip } from './ShotListEditor.styles';

/** Field caps from the shot-plan contract (spec 4.1); the backend truncates anything longer. */
const MUSIC_MAX = 240;
const CAMERA_MAX = 120;
const SOUND_MAX = 160;

export interface ShotListEditorProps {
  plan: ShotPlan;
  /** Read-only while composing / generating, and on a locked (rendered) version. */
  disabled?: boolean;
  /** Every edit hands back the whole patched plan (the composer forks a locked version first). */
  onChange: (plan: ShotPlan) => void;
}

/**
 * The easy-mode editor for a single-clip (v2) ad's shot plan: the music line, then one row per timed shot with
 * its action (what happens), camera and sound. Timings are read-only (the backend owns the timeline) and shots
 * cannot be added, removed or reordered in v2. The closing shot is the empty backdrop the logo and CTA are
 * composited over, so it is labelled as such.
 */
export const ShotListEditor = ({ plan, disabled, onChange }: ShotListEditorProps): React.ReactElement => {
  // Field / TextArea labels are props, invisible to the i18n extractor, so each copy sits in its own t() call.
  const { t } = useTranslation();
  return (
    <Root>
      <Field
        label={t('Music')}
        value={plan.music}
        maxLength={MUSIC_MAX}
        disabled={disabled}
        onChange={(music) => onChange({ ...plan, music })}
      />
      <Shots>
        {plan.shots.map((shot, i) => (
          // Shots never reorder / add / remove in v2, so the time range is a stable identity.
          <Shot key={`${shot.start_s}-${shot.end_s}`} $closing={shot.closing === true}>
            <ShotHead>
              <TimeChip raw text={`${shot.start_s}-${shot.end_s}s`} />
              {shot.closing ? (
                <ClosingTag text="Logo & CTA appear here" />
              ) : (
                <ShotLabel text="Shot {{n}}" placeholders={{ n: i + 1 }} />
              )}
            </ShotHead>
            <TextArea
              label={t('Action')}
              value={shot.action}
              rows={2}
              autoGrow
              disabled={disabled}
              onChange={(action) => onChange(updateShot(plan, i, { action }))}
            />
            <ShotFields>
              <Field
                label={t('Camera')}
                value={shot.camera}
                maxLength={CAMERA_MAX}
                disabled={disabled}
                onChange={(camera) => onChange(updateShot(plan, i, { camera }))}
              />
              <Field
                label={t('Sound')}
                value={shot.sound}
                maxLength={SOUND_MAX}
                disabled={disabled}
                onChange={(sound) => onChange(updateShot(plan, i, { sound }))}
              />
            </ShotFields>
          </Shot>
        ))}
      </Shots>
    </Root>
  );
};

export default ShotListEditor;
